import React, { useEffect, useRef, useState } from "react";
import { ClinicInnerButton } from "../clinic/clinicActionButton";
import { SkeletonLoader } from "../loader/skeletonLoader";
import { PatientFirstSummaryView } from "./patientFirstSummaryView";
import {
  PatientFirstSummaryFragment,
  PatientSummaryData,
  SavedCaseStatus,
  SummarySectionType,
} from "fe-common/graphql";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { PatientSummaryEdit } from "./patientSummaryEdit";
import { KAHUN_PLATFORM } from "user-management/src/components/loginForm";
import { ReactComponent as CopyIcon } from "../assets/copyIcon.svg";
import { ReactComponent as EditIcon } from "../assets/editIcon2.svg";
import { copyToClipboard } from "fe-common/utils/copyToClipboard";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { SectionAction } from "./types/SectionAction";
import { SummaryError, TextHighlight } from "fe-common/commonTypes/errors";
import { WebDao } from "fe-common/graphql/dao";
import ColumnHeader from "./ColumnHeader";
//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]
export interface PatientSummaryProps {
  caseId: string;
  clinicId: string;
  clinicName: string;
  platform: KAHUN_PLATFORM;
  summary: PatientSummaryData;
  loading?: boolean;
  onUpdate?: (etag?: string) => void;
  onCopy?: (ref: React.MutableRefObject<any>, message: string) => void;
  onError?: () => void;
  children?: React.ReactNode;
  sectionActions?: SectionAction[];
  hideTitle?: boolean;
  onNotify?: (message: string) => void;
  inExtension?: boolean;
  hideCopy?: boolean;
}

export type SummaryPIIValidation = {
  valid: boolean;
  messages?: string[];
  highlights?: TextHighlight[];
};

export const summaryToText = (summary: PatientFirstSummaryFragment) => {
  if (!summary) return "";
  const ignoreTypes = [
    SummarySectionType.Assessment,
    SummarySectionType.ReasonForVisit,
  ];
  const sections = summary?.sections || [];

  if (sections.length === 0) return "";

  const freeForm = sections.find(
    (section) => section.type === SummarySectionType.FreeForm,
  )?.content;
  if (freeForm) return freeForm;

  return sections
    .filter((section) => !ignoreTypes.includes(section.type))
    .map((section) => {
      return `${section.title}:\n${section.content}`;
    })
    .join("\n");
};
//endregion [[ Props ]]
export const checkSummaryForPII = async (
  inputText: string,
  webDao: WebDao,
): Promise<SummaryPIIValidation> => {
  const piiCheck = await webDao.checkForPII(inputText);
  if (piiCheck.status === SavedCaseStatus.Success) return { valid: true };

  const messages = piiCheck.errors.map((err: SummaryError) => {
    const fields = err.fields.map((f) => {
      const typeString = f.Type.replace(/_/g, " ");
      return (
        typeString.charAt(0).toUpperCase() + typeString.slice(1).toLowerCase()
      );
    });

    return `Please remove the following personal details from the case description:\n${[
      ...new Set(fields),
    ].join(", ")}`;
  });
  return {
    valid: false,
    messages,
    highlights: piiCheck.errors
      .filter((err: SummaryError) => err.fields?.length)
      .flatMap((err: SummaryError) =>
        err.fields?.flatMap((field) => ({
          BeginOffset: field.BeginOffset,
          EndOffset: field.EndOffset,
        })),
      ),
  };
};
//region [[ Functions ]]

//endregion [[ Functions ]]

export const PatientSummary = ({
  caseId,
  clinicId,
  clinicName,
  platform,
  summary: contextSummary,
  loading,
  onCopy,
  onUpdate,
  onError,
  ...props
}: PatientSummaryProps) => {
  const { translate } = useTranslateByGender();
  const [editMode, setEditMode] = useState(false);
  const [summary, setSummary] = useState<PatientSummaryData>(contextSummary);
  const containerRef = useRef(null);

  const handleCopyEntireSummaryClicked = () => {
    // including stuff injected as props.children
    copyToClipboard(containerRef.current);
    evt({
      category: CATEGORY.PATIENT_CARD,
      action: ACTION.CLINIC_COPY_PATIENT_SUMMARY,
      props: {
        clinicName,
        caseId,
        patientId: caseId,
        clinicId,
        platform,
        section: "all",
      },
    });
    // show toaster
    props.onNotify?.(`${translate("SUMMARY_TITLE")} ${translate("COPIED")}`);
  };

  const updateAssessment = (
    updatedSummary: PatientSummaryData,
    etag: string,
  ) => {
    onUpdate(etag);
    setSummary(updatedSummary);
    setEditMode(false);
  };

  useEffect(() => {
    if (!editMode) setSummary(contextSummary);
  }, [contextSummary]);

  if (editMode)
    return (
      <PatientSummaryEdit
        caseId={caseId}
        clinicId={clinicId}
        summary={summary}
        onUpdate={updateAssessment}
        onCancel={() => setEditMode(false)}
        onError={onError}
      />
    );

  const topButtons = props.sectionActions?.filter?.(
    (sa) => !sa.section || sa.section.split("|").some((s) => s.trim() === "*"),
  );

  return (
    <>
      {!loading && (
        <ColumnHeader
          title={!props.hideTitle && translate("SUMMARY_TITLE")}
          buttons={
            <>
              <ClinicInnerButton
                className="edit-summary"
                onClick={() => setEditMode(true)}
                icon={<EditIcon />}
                label={translate("EDIT")}
              />
              {!props.hideCopy && summary && (
                <ClinicInnerButton
                  className="copy-summary"
                  onClick={handleCopyEntireSummaryClicked}
                  icon={<CopyIcon />}
                  label={translate("COPY")}
                />
              )}
              {!!topButtons?.length &&
                topButtons.map((action) => (
                  <ClinicInnerButton
                    className={"general-section-action"}
                    onClick={(e) => action.onClick(e as any, null)}
                    label={action.text}
                    icon={action.icon}
                  />
                ))}
            </>
          }
        >
          {props.children}
        </ColumnHeader>
      )}
      <div ref={containerRef} className="column-content">
        {loading ? (
          <SkeletonLoader style={{ paddingTop: 16 }} />
        ) : (
          <>
            <PatientFirstSummaryView
              patientSummary={summary}
              showPatientInformation={false}
              supportedSections={[]}
              hiddenSections={["assessment"]}
              onNotify={props.onNotify}
              clinicId={clinicId}
              clinicName={clinicName}
              platform={platform}
              sectionActions={props.sectionActions}
              inExtension={props.inExtension}
            />
          </>
        )}
      </div>
    </>
  );
};
